import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import DataTable, {
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableHeader,
  DataTableCell,
} from '@amzn/meridian/data-table';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store';
import {
  selectStationAssignment,
  selectStationAssignmentData,
} from '../../../store/slices/stationAssignmentSlice';
import { selectStationCode } from '../../../store/slices/shiftPlanSlice';
import { showError } from '../../../store/slices/errorSlice';
import { PlanStatus, ROUTES } from '../../../utils/constants';
import { getPlannedHeadcount } from './api';
import { PlannedHeadcountPath, GetPlannedHeadcountResponse } from './models';
import RatesRosterLoader from '../../../components/RatesRosterLoader/RatesRosterLoader';
import { MAX_RETRIES, POLLING_INTERVAL_IN_MS } from '../../rosterAndRates/constants';
import './index.scss';

// Status that indicates the shift plan has been created and the planned
// headcount is ready to display.
const SUCCESS_STATUSES: PlanStatus[] = [
  PlanStatus.PENDING_APPROVAL,
];

// The Redux value used to indicate the second half of the shift is selected.
const SECOND_HALF_VALUE = 'secondHalfStationAssignment';

// Placeholder rendered for support-path cells that have no physical capacity.
const EMPTY_CELL = '—';

// ── Pure utility functions (exported for testability) ──

/**
 * Returns the planned headcount for the given path in the selected half.
 */
export function headcountForHalf(path: PlannedHeadcountPath, isSecondHalf: boolean): number {
  return isSecondHalf ? path.secondHalfHeadcount : path.firstHalfHeadcount;
}

/**
 * Sums the planned headcount across a set of paths for the selected half.
 */
export function sumHeadcount(paths: PlannedHeadcountPath[], isSecondHalf: boolean): number {
  return paths.reduce((total, path) => total + headcountForHalf(path, isSecondHalf), 0);
}

/**
 * Sums an optional numeric field (lines / stations) across a set of paths,
 * ignoring paths where the field is not defined.
 */
export function sumField(paths: PlannedHeadcountPath[], field: 'lines' | 'stations'): number {
  return paths.reduce((total, path) => total + (path[field] ?? 0), 0);
}

// ── Component ──

const PlannedHeadcountPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { planId } = useAppSelector(selectStationAssignment);
  const { selectedHalf } = useAppSelector(selectStationAssignmentData);
  const warehouseId = useAppSelector(selectStationCode);

  const [primaryPaths, setPrimaryPaths] = useState<PlannedHeadcountPath[]>([]);
  const [supportPaths, setSupportPaths] = useState<PlannedHeadcountPath[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  const isSecondHalf = selectedHalf === SECOND_HALF_VALUE;

  // Track polling retries across renders without recreating the fetch callback.
  const retryCountRef = useRef<number>(0);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Fetches the planned headcount data.
   *
   * Mirrors the Shift Roster flow: while the backend reports PENDING_CREATION the
   * plan is still being built in the background, so we keep the loader up and
   * signal the caller to continue polling. Once a success status arrives we
   * render the tables; any other status (or exhausted retries) surfaces an error
   * and stops polling.
   *
   * @returns `true` when polling should stop, `false` to keep polling.
   */
  const fetchData = useCallback(async (): Promise<boolean> => {
    if (!warehouseId || !planId) {
      dispatch(showError({
        title: 'Missing Parameters',
        message: 'Missing required parameters (warehouseId or planId).',
      }));
      setInitialLoading(false);
      return true;
    }

    try {
      const response: GetPlannedHeadcountResponse = await getPlannedHeadcount(
        warehouseId,
        planId,
      );

      // Plan is still being created in the background – keep the loader up and continue polling.
      if (response.planStatus === PlanStatus.PENDING_CREATION) {
        if (retryCountRef.current >= MAX_RETRIES) {
          setInitialLoading(false);
          dispatch(showError({
            title: 'Error',
            message: 'Timed out while the planned headcount was being created. Please try again later.',
          }));
          return true;
        }
        retryCountRef.current += 1;
        return false; // Continue polling
      }

      // Any status other than a known success status (FAILED / REJECTED / etc.) is an error.
      if (response.planStatus && !SUCCESS_STATUSES.includes(response.planStatus)) {
        setInitialLoading(false);
        dispatch(showError({
          title: 'Error',
          message: 'Failed to load planned headcount data. Please try again.',
        }));
        return true;
      }

      // Success (or a legacy response without planStatus): render the tables.
      if (response.primaryPaths.length === 0 && response.supportPaths.length === 0) {
        dispatch(showError({
          title: 'No Data',
          message: 'Failed to load planned headcount data. Please try again.',
        }));
      } else {
        setPrimaryPaths(response.primaryPaths);
        setSupportPaths(response.supportPaths);
      }
      setInitialLoading(false);
      return true;
    } catch (err: any) {
      dispatch(showError({
        title: 'Error',
        message: err.message || 'An unexpected error occurred.',
      }));
      setInitialLoading(false);
      return true;
    }
  }, [warehouseId, planId, dispatch]);

  // Initial load: fetch once and, while the plan is still being created, poll until it is ready.
  // Uses a recursive setTimeout (not setInterval) so the next poll only starts after the previous
  // fetch resolves — this avoids overlapping in-flight requests. The `cancelled` flag stops any
  // in-flight continuation from scheduling a new poll after the effect is cleaned up.
  useEffect(() => {
    let cancelled = false;
    retryCountRef.current = 0;

    const poll = async () => {
      const shouldStop = await fetchData();
      if (cancelled || shouldStop) {
        return;
      }
      pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL_IN_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [fetchData]);

  const handleCancel = () => {
    navigate('/');
  };

  const handleVerify = () => {
    navigate(ROUTES.SHIFT_ROTATION_ASSIGNMENTS);
  };

  // ── Loading state ──
  // Uses the same full-screen loader as the Shift Roster while the plan is being created.
  if (initialLoading) {
    return (
      <Box className="planned-headcount-page">
        <div data-testid="planned-headcount-loader">
          <RatesRosterLoader type="roster" status="loading" />
        </div>
      </Box>
    );
  }

  // ── Derived summary values ──
  const primaryHeadcount = sumHeadcount(primaryPaths, isSecondHalf);
  const supportHeadcount = sumHeadcount(supportPaths, isSecondHalf);
  const totalHeadcount = primaryHeadcount + supportHeadcount;
  const totalStations = sumField(primaryPaths, 'stations');

  const totalPrimaryLines = sumField(primaryPaths, 'lines');
  const totalPrimaryStations = sumField(primaryPaths, 'stations');

  const summaryCards: { label: string; value: number; sub: string }[] = [
    { label: 'Total HC', value: totalHeadcount, sub: 'All paths combined' },
    { label: 'Primary HC', value: primaryHeadcount, sub: `${primaryPaths.length} process paths` },
    { label: 'Support HC', value: supportHeadcount, sub: `${supportPaths.length} support roles` },
    { label: 'Total Stations', value: totalStations, sub: 'Physical capacity' },
  ];

  return (
    <Box className="planned-headcount-page" spacingInset="400" data-testid="planned-headcount-page">
      {/* Summary cards */}
      <Row
        spacing="300"
        widths="fill"
        alignmentVertical="stretch"
        wrap="down"
        className="planned-headcount-summary"
      >
        {summaryCards.map((card) => (
          <Box
            key={card.label}
            type="outline"
            spacingInset="400"
            className="planned-headcount-card"
            data-testid={`summary-card-${card.label}`}
          >
            <Column spacing="100" alignmentHorizontal="center">
              <Text type="b100" color="secondary">{card.label.toUpperCase()}</Text>
              <div className="planned-headcount-card-value">
                <Heading type="d50" level={3}>{card.value}</Heading>
              </div>
              <Text type="b100" color="secondary">{card.sub}</Text>
            </Column>
          </Box>
        ))}
      </Row>

      {/* Primary paths */}
      <Box spacingInset="none 000 300 000">
        <Heading type="h300" level={2}>Primary paths</Heading>
      </Box>
      <DataTable
        layout="fixed"
        showRowDividers={true}
        data-testid="primary-paths-table"
      >
        <DataTableHead>
          <DataTableRow>
            <DataTableHeader>Process path</DataTableHeader>
            <DataTableHeader>FCLM ID</DataTableHeader>
            <DataTableHeader textAlign="end">Lines</DataTableHeader>
            <DataTableHeader textAlign="end">Stations</DataTableHeader>
            <DataTableHeader textAlign="end">Planned HC</DataTableHeader>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {primaryPaths.map((path) => (
            <DataTableRow key={path.functionId} data-testid={`primary-row-${path.functionId}`}>
              <DataTableCell>{path.name}</DataTableCell>
              <DataTableCell>{path.functionId}</DataTableCell>
              <DataTableCell textAlign="end">{path.lines ?? EMPTY_CELL}</DataTableCell>
              <DataTableCell textAlign="end">{path.stations ?? EMPTY_CELL}</DataTableCell>
              <DataTableCell textAlign="end">{headcountForHalf(path, isSecondHalf)}</DataTableCell>
            </DataTableRow>
          ))}
          <DataTableRow data-testid="primary-total-row">
            <DataTableCell><Text type="h100">Total</Text></DataTableCell>
            <DataTableCell>{''}</DataTableCell>
            <DataTableCell textAlign="end"><Text type="h100">{totalPrimaryLines}</Text></DataTableCell>
            <DataTableCell textAlign="end"><Text type="h100">{totalPrimaryStations}</Text></DataTableCell>
            <DataTableCell textAlign="end"><Text type="h100">{primaryHeadcount}</Text></DataTableCell>
          </DataTableRow>
        </DataTableBody>
      </DataTable>

      {/* Support paths */}
      <Box spacingInset="400 000 300 000">
        <Heading type="h300" level={2}>Support paths</Heading>
      </Box>
      <DataTable
        layout="fixed"
        showRowDividers={true}
        data-testid="support-paths-table"
      >
        <DataTableHead>
          <DataTableRow>
            <DataTableHeader>Process path</DataTableHeader>
            <DataTableHeader>FCLM ID</DataTableHeader>
            <DataTableHeader textAlign="end">Lines</DataTableHeader>
            <DataTableHeader textAlign="end">Stations</DataTableHeader>
            <DataTableHeader textAlign="end">Planned HC</DataTableHeader>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {supportPaths.map((path) => (
            <DataTableRow key={path.functionId} data-testid={`support-row-${path.functionId}`}>
              <DataTableCell>{path.name}</DataTableCell>
              <DataTableCell>{path.functionId}</DataTableCell>
              <DataTableCell textAlign="end">{path.lines ?? EMPTY_CELL}</DataTableCell>
              <DataTableCell textAlign="end">{path.stations ?? EMPTY_CELL}</DataTableCell>
              <DataTableCell textAlign="end">{headcountForHalf(path, isSecondHalf)}</DataTableCell>
            </DataTableRow>
          ))}
          <DataTableRow data-testid="support-total-row">
            <DataTableCell><Text type="h100">Total</Text></DataTableCell>
            <DataTableCell>{''}</DataTableCell>
            <DataTableCell textAlign="end"><Text type="h100">{EMPTY_CELL}</Text></DataTableCell>
            <DataTableCell textAlign="end"><Text type="h100">{EMPTY_CELL}</Text></DataTableCell>
            <DataTableCell textAlign="end"><Text type="h100">{supportHeadcount}</Text></DataTableCell>
          </DataTableRow>
        </DataTableBody>
      </DataTable>

      {/* Footer */}
      <Row alignmentHorizontal="end" spacingInset="400 000 000 000" spacing="300">
        <Button type="secondary" onClick={handleCancel} data-testid="cancel-button">
          Cancel
        </Button>
        <Button type="primary" onClick={handleVerify} data-testid="verify-button">
          Verify
        </Button>
      </Row>
    </Box>
  );
};

export default PlannedHeadcountPage;

import './index.scss';